import React, { useState, useEffect, useRef } from 'react';
// DO NOT import './DynamicTextAnimator.css'; for this extreme test.

const DynamicTextAnimator = ({ words, interval = 1000 }) => { // wordClassName prop removed for this test
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false); // To control blur/fade stages
  const containerRef = useRef(null);

  // Base style for the gradient visual effect
  const baseGradientStyle = {
    display: 'inline-block',
    width: '100%',
    textAlign: 'center',
    color: 'transparent',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    backgroundImage: 'linear-gradient(135deg, #FC10F2, #3078E4)',
    fontSize: '30px', // From parent's md:text-3xl
    lineHeight: '1.2', // Default
    // Properties for smooth transition (applied via JS inline)
    // These values will be changed by isAnimating state
    transition: 'opacity 0.4s ease-in-out, filter 0.4s ease-in-out', // For JS style changes
  };

  useEffect(() => {
    if (containerRef.current && words && words.length > 0) {
      let maxHeight = 0;
      const tempSpan = document.createElement('span');
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.fontSize = baseGradientStyle.fontSize;
      tempSpan.style.lineHeight = baseGradientStyle.lineHeight;
      document.body.appendChild(tempSpan);
      words.forEach(word => {
        tempSpan.textContent = word || "Default";
        if (tempSpan.offsetHeight > maxHeight) {
          maxHeight = tempSpan.offsetHeight;
        }
      });
      document.body.removeChild(tempSpan);
      if (maxHeight > 0) {
        containerRef.current.style.height = `${maxHeight}px`;
      } else {
        containerRef.current.style.minHeight = '24px';
        console.warn("[DynamicTextAnimator] Measured height was 0 for words, using fallback minHeight.");
      }
    } else if (containerRef.current) { // Ensure container has height even for default/fallback word
      containerRef.current.style.minHeight = '36px'; // Approximate for 30px font
      console.warn("[DynamicTextAnimator] No words, setting default container minHeight.");
    }
  }, [words, baseGradientStyle.fontSize, baseGradientStyle.lineHeight]);

  useEffect(() => {
    if (!words || words.length === 0) return;

    const animationEffectDuration = 400; // Duration of the blur/fade effect (ms)
    const timer = setInterval(() => {
      setIsAnimating(true); // Trigger blur out / fade out for current
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % words.length);
        setIsAnimating(false); // New current word should now blur in / fade in
      }, animationEffectDuration);
    }, interval); // Total interval including animation

    return () => clearInterval(timer);
  }, [words, interval]);

  if (!words || words.length === 0) {
    // Fallback if no words are provided
    const fallbackStyle = {
      ...baseGradientStyle,
      opacity: 1,
      filter: 'blur(0px)',
    };
    return (
      <span ref={containerRef} style={{ display: 'inline-block', verticalAlign: 'bottom', minHeight: baseGradientStyle.lineHeight > 1 ? `calc(${baseGradientStyle.fontSize} * ${baseGradientStyle.lineHeight})` : baseGradientStyle.fontSize }}>
        <span style={fallbackStyle}>Fallback</span>
      </span>
    );
  }

  const wordToDisplay = words[currentIndex] || "Text";

  // Define styles based on animation state
  let wordStyle;

  if (isAnimating) {
    // Current word is blurring/fading OUT
    wordStyle = {
      ...baseGradientStyle,
      opacity: 0,
      filter: 'blur(5px)', // Adjust blur amount as desired
    };
  } else {
    // Current word is sharp and visible (or just finished fading IN)
    wordStyle = {
      ...baseGradientStyle,
      opacity: 1,
      filter: 'blur(0px)',
    };
  }

  return (
    <span 
      ref={containerRef} 
      className="dynamic-text-animator-container-debug" // Unique class for easy finding
      style={{ 
        display: 'inline-block', 
        position: 'relative', 
        overflow: 'visible', // Changed to visible to allow blur to extend slightly
        verticalAlign: 'bottom',
        minHeight: '24px' 
      }}
    >
      {/* 
        We use a single span and rely on React re-rendering with new style
        for the blur/fade effect. A true cross-fade with two spans and JS-only
        smooth transitions is more complex. This gives a fade-out/blur-out
        then fade-in/blur-in effect for the *same* span as its content changes.
      */}
      <span
        key={currentIndex} // Key change will trigger re-render and transition if CSS transition is present
        style={wordStyle}
      >
        {wordToDisplay}
      </span>
    </span>
  );
};

export default DynamicTextAnimator; 